import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, X, Loader2, MessageSquare, Wand2, CheckCircle2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate stable session id per browser (persisted in localStorage)
const getSessionId = () => {
  let id = localStorage.getItem('chaser_ai_session');
  if (!id) {
    id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('chaser_ai_session', id);
  }
  return id;
};

/**
 * AIAssistant - Floating chatbot + question generator.
 * Two modes:
 *  - "chat": Q&A conversation with Claude Sonnet 4.5
 *  - "generate": Generate 25 MCQs on a topic (optionally save to course DB)
 *
 * Props:
 *  - courseId (optional): pre-fills context for course-specific interactions
 *  - courseName (optional): human-readable course name
 */
export const AIAssistant = ({ courseId, courseName }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('chat'); // 'chat' | 'generate'

  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Generate state
  const [genTopic, setGenTopic] = useState('');
  const [genSaveToDb, setGenSaveToDb] = useState(!!courseId);
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [genError, setGenError] = useState(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendChat = async (e) => {
    e?.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMsg = { role: 'user', content: chatInput.trim() };
    setMessages(prev => [...prev, userMsg, { role: 'assistant', content: '' }]);
    const messageToSend = chatInput.trim();
    setChatInput('');
    setChatLoading(true);

    try {
      const context = courseId ? `הלומד נמצא כרגע בקורס: ${courseName || courseId}` : null;
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message: messageToSend, context }),
      });
      if (!res.ok) throw new Error('שגיאת שרת');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        assistantContent += decoder.decode(value, { stream: true });
        setMessages(prev => {
          const next = [...prev];
          next[next.length - 1] = { role: 'assistant', content: assistantContent };
          return next;
        });
      }
    } catch (err) {
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', content: `⚠️ שגיאה: ${err.message}` };
        return next;
      });
    } finally {
      setChatLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e?.preventDefault();
    if (!genTopic.trim() || genLoading) return;

    setGenLoading(true);
    setGenError(null);
    setGenResult(null);

    try {
      // 1. Kick off async job
      const res = await fetch(`${API}/ai/generate-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: genTopic.trim(),
          course_id: courseId || null,
          save_to_db: genSaveToDb,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'שגיאה ביצירת השאלות');

      const jobId = data.job_id;
      // 2. Poll every 3s until done (max ~5 min)
      const maxAttempts = 100;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const statusRes = await fetch(`${API}/ai/generate-questions/status/${jobId}`);
        const statusData = await statusRes.json();
        if (statusData.status === 'done') {
          setGenResult({
            count: statusData.count,
            questions: statusData.questions,
            saved_to_db: statusData.saved_to_db,
            duplicates_skipped: statusData.duplicates_skipped,
          });
          return;
        }
        if (statusData.status === 'error') {
          throw new Error(statusData.error || 'שגיאה ביצירת השאלות');
        }
      }
      throw new Error('הבקשה לוקחת יותר מדי זמן, אנא נסי שוב');
    } catch (err) {
      setGenError(err.message);
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-40 flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white px-5 py-3 rounded-full shadow-2xl hover:shadow-fuchsia-500/50 hover:scale-105 transition-all font-bold text-sm md:text-base group"
        data-testid="ai-assistant-open-btn"
        aria-label="סוכן AI"
      >
        <Sparkles className="w-5 h-5 md:w-6 md:h-6 group-hover:animate-pulse" />
        <span className="hidden md:inline">סוכן AI</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="ai-assistant-modal"
        >
          <div
            className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6" />
                <div>
                  <h3 className="font-bold text-lg">סוכן AI של הצ&apos;ייסר</h3>
                  <p className="text-xs opacity-80">Claude Sonnet 4.5 • מדבר עברית</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
                data-testid="ai-assistant-close-btn"
                aria-label="סגור"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mode tabs */}
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => setMode('chat')}
                className={`flex-1 py-3 px-4 font-bold text-sm transition-colors flex items-center justify-center gap-2 ${mode === 'chat' ? 'bg-violet-50 text-violet-700 border-b-2 border-violet-600' : 'text-slate-500 hover:bg-slate-50'}`}
                data-testid="ai-tab-chat"
              >
                <MessageSquare className="w-4 h-4" /> שאל שאלה
              </button>
              <button
                onClick={() => setMode('generate')}
                className={`flex-1 py-3 px-4 font-bold text-sm transition-colors flex items-center justify-center gap-2 ${mode === 'generate' ? 'bg-fuchsia-50 text-fuchsia-700 border-b-2 border-fuchsia-600' : 'text-slate-500 hover:bg-slate-50'}`}
                data-testid="ai-tab-generate"
              >
                <Wand2 className="w-4 h-4" /> צור 25 שאלות
              </button>
            </div>

            {/* Content */}
            {mode === 'chat' ? (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]" data-testid="ai-chat-messages">
                  {messages.length === 0 && (
                    <div className="text-center py-8">
                      <Sparkles className="w-12 h-12 text-violet-400 mx-auto mb-3" />
                      <h4 className="font-bold text-slate-900 mb-2">איך אפשר לעזור?</h4>
                      <p className="text-slate-500 text-sm mb-4">אני יכול להסביר שאלות שנפלת בהן, לענות על שאלות מקצועיות, או להדריך באפליקציה.</p>
                      <div className="flex flex-wrap justify-center gap-2 text-xs">
                        <button onClick={() => setChatInput('הסבר לי איך פועלת מערכת הרנין-אנגיוטנסין')} className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full hover:bg-violet-200">מערכת הרנין-אנגיוטנסין</button>
                        <button onClick={() => setChatInput('מה ההבדל בין סטרטגיה של UTV לבין CTV באיסוף עדויות?')} className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full hover:bg-violet-200">UTV מול CTV</button>
                        <button onClick={() => setChatInput('כמה שאלות יש בסימולטור המבחן?')} className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full hover:bg-violet-200">איך משתמשים באפליקציה?</button>
                      </div>
                    </div>
                  )}

                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
                      data-testid={`ai-message-${msg.role}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          msg.role === 'user'
                            ? 'bg-violet-600 text-white'
                            : 'bg-slate-100 text-slate-900'
                        }`}
                      >
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {msg.content}
                          {msg.role === 'assistant' && !msg.content && chatLoading && (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendChat} className="border-t border-slate-200 p-3 flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="הקלד שאלה..."
                    className="flex-1 px-4 py-2 rounded-full border border-slate-200 focus:border-violet-500 focus:outline-none"
                    disabled={chatLoading}
                    data-testid="ai-chat-input"
                  />
                  <button
                    type="submit"
                    disabled={chatLoading || !chatInput.trim()}
                    className="bg-violet-600 hover:bg-violet-700 text-white p-2 rounded-full disabled:opacity-50"
                    data-testid="ai-chat-send-btn"
                  >
                    {chatLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </form>
              </>
            ) : (
              /* Generate Questions Mode */
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <h4 className="font-bold text-slate-900 mb-2">יצירת 25 שאלות ברמות שונות</h4>
                  <p className="text-slate-500 text-sm">Claude Sonnet 4.5 ייצור עבורך 25 שאלות אמריקאיות איכותיות (8 קל, 9 בינוני, 8 קשה) בנושא שתבקש.</p>
                </div>

                <form onSubmit={handleGenerate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-1">נושא השאלות *</label>
                    <input
                      type="text"
                      value={genTopic}
                      onChange={(e) => setGenTopic(e.target.value)}
                      placeholder="לדוגמה: אי ספיקת לב שמאלית, אתיקה רפואית, פרמקוקינטיקה של אופיואידים..."
                      className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-fuchsia-500 focus:outline-none"
                      required
                      minLength={3}
                      disabled={genLoading}
                      data-testid="ai-gen-topic-input"
                    />
                    {courseName && (
                      <p className="text-xs text-slate-500 mt-1">הקורס הנוכחי: <strong>{courseName}</strong></p>
                    )}
                  </div>

                  {courseId && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={genSaveToDb}
                        onChange={(e) => setGenSaveToDb(e.target.checked)}
                        className="w-5 h-5 text-fuchsia-600 rounded"
                        data-testid="ai-gen-save-checkbox"
                      />
                      <span className="text-sm text-slate-700">שמור למאגר של הקורס (עם בדיקת כפילויות)</span>
                    </label>
                  )}

                  <button
                    type="submit"
                    disabled={genLoading || !genTopic.trim()}
                    className="w-full bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-700 hover:to-violet-700 text-white font-bold py-3 rounded-full disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="ai-gen-submit-btn"
                  >
                    {genLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        מייצר... (עשוי לקחת 30-60 שניות)
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5" />
                        צור 25 שאלות
                      </>
                    )}
                  </button>
                </form>

                {genError && (
                  <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex gap-2" data-testid="ai-gen-error">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">{genError}</div>
                  </div>
                )}

                {genResult && (
                  <div className="mt-6 space-y-4" data-testid="ai-gen-result">
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      <div className="text-sm font-bold">
                        נוצרו {genResult.count} שאלות בהצלחה!
                        {genResult.saved_to_db > 0 && ` • נשמרו למאגר: ${genResult.saved_to_db}`}
                        {genResult.duplicates_skipped > 0 && ` • כפילויות שדולגו: ${genResult.duplicates_skipped}`}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {genResult.questions.map((q, i) => (
                        <div key={i} className="bg-slate-50 rounded-2xl p-4 border border-slate-200" data-testid={`ai-gen-question-${i}`}>
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="font-bold text-slate-900 text-sm flex-1">
                              <span className="text-fuchsia-600">שאלה {i + 1}:</span> {q.question}
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                              q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                              q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {q.difficulty === 'easy' ? 'קל' : q.difficulty === 'medium' ? 'בינוני' : 'קשה'}
                            </span>
                          </div>
                          <ol className="space-y-1 mb-3 mr-4" style={{ listStyleType: 'hebrew' }}>
                            {q.options.map((opt, j) => (
                              <li
                                key={j}
                                className={`text-sm ${j === q.correct_answer ? 'text-green-700 font-bold' : 'text-slate-700'}`}
                              >
                                {['א.', 'ב.', 'ג.', 'ד.'][j]} {opt} {j === q.correct_answer && '✓'}
                              </li>
                            ))}
                          </ol>
                          <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-700">
                            <strong className="text-fuchsia-600">הסבר:</strong> {q.explanation}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
